// Cluster ZUWEISEN — reine, DB-/DOM-freie Logik des Zuweisen-Popups
// (Muster mailCommandState.ts). Das Popup selbst rendert nur; alles, was man
// ohne Browser prüfen kann, steht hier.
//
// Zwei Wege statt fünf Pillen (B1):
//   Weg 1 „Bestehenden Kontakt suchen" — Treffer wählen, optional die
//         Absenderadresse am Kontakt hinterlegen, zuweisen.
//   Weg 2 „Neuen Kontakt erstellen"    — Frage 1 Firma/Privat (C2), Frage 2
//         Interessent/Kunde (C1), danach die Minimalfelder (C6).
//
// Die Zuweisung ist ein ABSCHLIESSBARER Vorgang (B5): `MailZuweisungErgebnis`
// trägt `weiter: 'zuweisen' | 'vorgang'`. Der Ausgang „zuweisen" schreibt den
// MailLink und archiviert (mailZuweisungAbschluss.ts), der Ausgang „vorgang"
// führt wie bisher in den Erstell-Flow.
//
// Was hier NICHT mehr steht: `AssignBranch` und die drei `minimal*`-Prüfungen
// samt ihren Payload-Buildern. Sie gehörten zur Fünf-Pillen-Maske und
// verlangten E-Mail als Pflichtfeld — strenger als die Vollanlage (C6).

import type { VorgangFuerVorgabe } from "../vorgang/create/vorgangFuer";
import { verantToItems, type MenschItem } from "../verantwortlichkeit/buildMenschenItems";
import type { VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";
import type { CreatedPerson } from "../../services/menschenFuersUnternehmenService";

/** Ein Kontaktbezug, wie ihn Popup, Command-Leiste und Erstell-Flow teilen. */
export type KontaktRef = { id: number; displayName: string };

/** Ergebnis der Zuweisung: der gewählte/angelegte Kontakt — oder `null` (ohne Kunden). */
export type AssignResolution = KontaktRef | null;

/** FO/BO wie es OwnershipSection in die FormState schreibt. */
export type AssignOwnership = {
    foUserId?: string;
    boType?: string;
    boUserId?: string;
    boTeamId?: string;
};

/** Nur die gesetzten FO/BO-Felder in den Contact-Body übernehmen (leere weglassen). */
export function ownershipBody(own: AssignOwnership): Record<string, string> {
    const out: Record<string, string> = {};
    if (own.foUserId) out.foUserId = own.foUserId;
    if (own.boType) out.boType = own.boType;
    if (own.boType === "USER" && own.boUserId) out.boUserId = own.boUserId;
    if (own.boType === "TEAM" && own.boTeamId) out.boTeamId = own.boTeamId;
    return out;
}

/** FO ODER BO gesetzt (Anlage-Voraussetzung, wie in der Vollanlage). */
export function ownershipSet(own: AssignOwnership): boolean {
    return (
        !!own.foUserId ||
        (own.boType === "TEAM" && !!own.boTeamId) ||
        (own.boType === "USER" && !!own.boUserId)
    );
}

/** Absender-Adresse als vorbefüllte E-Mail (lowercase, getrimmt) — leer bleibt leer. */
export function prefillEmail(senderEmail: string | null | undefined): string {
    return (senderEmail ?? "").trim().toLowerCase();
}

/**
 * Rechtsform-/Firmenwörter, an denen ein Absendername als FIRMA erkennbar ist
 * („Muster Bau GmbH", „Info | Beispiel AG"). Bewusst grob — es geht nur darum,
 * aus einem Firmennamen keinen Herrn „GmbH" zu machen.
 */
const FIRMEN_WORT =
    /(^|[\s,(])(gmbh|mbh|ag|kg|kgaa|ohg|gbr|ug|se|e\.\s?v\.?|e\.\s?k\.?|inc\.?|ltd\.?|llc|co\.|holding|gruppe|group|versicherung(en)?|verwaltung|stiftung)(?=$|[\s,.)&])/i;

/** Funktionspostfächer („Info", „Kundenservice") — weder Person noch Firmenname. */
const FUNKTIONS_WORT =
    /(^|[\s,(])(info|team|service|kundenservice|support|buchhaltung|office|kontakt|noreply|no-reply)(?=$|[\s,.)&])/i;

/** Wirkt der Absendername wie eine Firma? */
export function istFirmenName(name: string | null | undefined): boolean {
    return FIRMEN_WORT.test((name ?? "").trim());
}

/**
 * Den Absendernamen von Beiwerk befreien: Anführungszeichen, Zusätze in
 * Klammern und alles hinter „ | " bzw. „ - " („Max Muster | Beispiel AG").
 */
function bereinigeAbsenderName(name: string): string {
    return name
        .replace(/^["'\s]+|["'\s]+$/g, "")
        .replace(/\s*\([^)]*\)\s*/g, " ")
        .split(/\s+[|–—-]\s+/)[0]
        .trim();
}

/**
 * Absender-Anzeigename als Vorschlag für Vor-/Nachname (Person/AP-Zweig):
 * letztes Wort = Nachname, der Rest = Vorname; ein einzelnes Wort → Nachname.
 * Eine reine E-Mail-Adresse (enthält „@") liefert KEINEN Namensvorschlag.
 *
 * PO 28.09.2026 („Kontakt aus Mail einfacher"): Outlook in Firmen schreibt
 * oft „Nachname, Vorname" — daraus wurde bisher der Vorname „Nachname," und
 * der Nachname „Vorname". Ein Firmenname („Beispiel GmbH") ist keine Person
 * und liefert keinen Namensvorschlag.
 */
export function splitSenderName(senderName: string | null | undefined): {
    firstName: string;
    lastName: string;
} {
    const roh = (senderName ?? "").trim();
    if (!roh || roh.includes("@")) return { firstName: "", lastName: "" };
    const n = bereinigeAbsenderName(roh);
    if (!n || istFirmenName(n) || FUNKTIONS_WORT.test(n)) return { firstName: "", lastName: "" };
    const komma = n.split(",").map((s) => s.trim());
    if (komma.length === 2 && komma[0] && komma[1]) {
        return { firstName: komma[1], lastName: komma[0] };
    }
    const parts = n.split(/\s+/);
    if (parts.length === 1) return { firstName: "", lastName: parts[0] };
    return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] };
}

/** Domänen, bei denen „Firma" als Vorauswahl unsinnig wäre (Frage 1, C2). */
export const FREEMAIL_DOMAINS = new Set([
    "gmail.com", "googlemail.com", "web.de", "gmx.de", "gmx.net", "gmx.at", "gmx.ch",
    "t-online.de", "outlook.de", "outlook.com", "hotmail.de", "hotmail.com", "live.de",
    "yahoo.de", "yahoo.com", "aol.com", "icloud.com", "me.com", "posteo.de", "mailbox.org",
    "freenet.de", "arcor.de", "online.de",
]);

/**
 * Firmenname aus der Absender-Domäne raten — nur als VORSCHLAG, wenn weder
 * Signatur noch Absendername einen hergeben: „info@mueller-bau.de" →
 * „Mueller-Bau". Freemail und eigene Domänen liefern nichts.
 */
export function firmennameAusDomain(
    senderEmail: string | null | undefined,
    interneDomaenen: Iterable<string> = [],
): string {
    const a = (senderEmail ?? "").trim().toLowerCase();
    const at = a.lastIndexOf("@");
    if (at < 0) return "";
    const domain = a.slice(at + 1);
    if (!domain || FREEMAIL_DOMAINS.has(domain) || new Set(interneDomaenen).has(domain)) return "";
    const teile = domain.split(".").filter(Boolean);
    if (teile.length < 2) return "";
    // Zweitletztes Label; bei „co.uk"/„com.de"-artigen Endungen eins weiter vorn.
    let label = teile[teile.length - 2];
    if (label.length <= 3 && teile.length >= 3 && /^(co|com|org|net|gv|ac)$/.test(label)) {
        label = teile[teile.length - 3];
    }
    return label
        .split("-")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join("-");
}

/** Strukturierte Art (SP1, ASCII) aus dem Radio-Label 'Privat'/'Geschäftlich'. */
export function kindFromLabel(label: string | null | undefined): "privat" | "geschaeftlich" | undefined {
    const l = (label ?? "").toLowerCase();
    if (l === "privat") return "privat";
    if (l === "geschäftlich" || l === "geschaeftlich") return "geschaeftlich";
    return undefined;
}

// ── Minimal-AP an bestehende Firma (attach-menschen) ──────────────────────────

/** Fester Schlüssel, unter dem der Server den neuen AP zurückmeldet. */
export const AP_NEW_PERSON_KEY = "sp4-ap";

/** Die neu angelegte AP-id aus der attach-menschen-Antwort ziehen (Key zuerst, sonst der erste). */
export function resolveCreatedApId(created: CreatedPerson[]): number | null {
    if (!created.length) return null;
    const keyed = created.find((p) => p.newPersonKey === AP_NEW_PERSON_KEY);
    return (keyed ?? created[0]).contactId;
}

// ── Übergabe an das Light-Popup ───────────────────────────────────────────────

/** Anzeigename eines gewählten Bestandskontakts (GET /contact liefert lastname/firstName/displayName). */
export function contactDisplayName(c: {
    displayName?: string | null;
    lastname?: string | null;
    firstName?: string | null;
    vorname?: string | null;
}): string {
    if (c.displayName && c.displayName.trim()) return c.displayName.trim();
    const last = (c.lastname ?? "").trim();
    const first = (c.firstName ?? c.vorname ?? "").trim();
    return first ? `${last}, ${first}` : last || "Kontakt";
}

/** Extrahiert die neue Kontakt-id aus einer POST /contact-Antwort (Contact oder {contact}). */
export function extractCreatedContactId(created: unknown): number | null {
    const anyC = created as { id?: unknown; contact?: { id?: unknown } } | null;
    const id = anyC?.id ?? anyC?.contact?.id;
    return typeof id === "number" ? id : null;
}

// ── Absender-Mail an einen BESTEHENDEN Kontakt anhängen (F1b) ─────────────────

/** Eine Kommunikationsweg-Zeile eines Kontakts (GET /contact/:id?include=communications). */
export type ContactCommRow = {
    id?: number;
    type: string;
    label?: string | null;
    value: string;
    isDefault?: boolean;
};

/** Ist die Adresse (case-insensitiv) bereits als E-Mail am Kontakt hinterlegt? */
export function hasSameEmail(comms: ContactCommRow[] | undefined, value: string): boolean {
    const v = (value ?? "").trim().toLowerCase();
    if (!v) return false;
    return (comms ?? []).some(
        (c) => c.type === "email" && (c.value ?? "").trim().toLowerCase() === v,
    );
}

/** Existiert bereits eine Default-E-Mail? (UQ: nur EINE je Kontakt & Typ.) */
export function hasDefaultEmail(comms: ContactCommRow[] | undefined): boolean {
    return (comms ?? []).some((c) => c.type === "email" && !!c.isDefault);
}

// ── B6: eigene Domäne — die MAIL ja, die ADRESSE nie ────────────────────────
// Schreibt ein Kollege von @courte.de an einen Kunden und man weist die Mail
// zu, landete bisher die MITARBEITER-Adresse als Kommunikationsweg am
// Kundenkontakt. Das ist immer falsch: Die Mail gehört zum Kunden, die Adresse
// gehört dem Kollegen. Deshalb wird die Übernahme hart unterbunden — die Mail
// selbst wird stattdessen dem Kontakt zugewiesen (B3).
//
// Bewusst die AUSSCHLIESSENDE Richtung: Eine gefälschte @courte.de-Absenderzeile
// führt hier nur dazu, dass eine Adresse NICHT übernommen wird — harmlos.
// Umgekehrt dürfte dieses Signal nie ein Recht begründen (vgl.
// `apps/api/src/mail/internal-domains.ts`).

/** Die klein geschriebene Domäne einer E-Mail-Adresse (`null`, wenn keine). */
export function domaeneVon(address: string | null | undefined): string | null {
    if (!address) return null;
    const at = address.lastIndexOf("@");
    if (at < 0 || at === address.length - 1) return null;
    return address.slice(at + 1).trim().toLowerCase() || null;
}

/**
 * Die eigenen Domänen, abgeleitet aus den Postfächern des Mandanten — dieselbe
 * Quelle wie im Backend (`internalDomainsFromMailboxes`). Wer ein Postfach
 * @firma.de hat, dessen Firma ist @firma.de.
 */
export function interneDomaenenAusPostfaechern(
    mailboxAddresses: readonly (string | null | undefined)[],
): Set<string> {
    const out = new Set<string>();
    for (const adresse of mailboxAddresses) {
        const dom = domaeneVon(adresse);
        if (dom) out.add(dom);
    }
    return out;
}

/** Gehört die Adresse zur eigenen Organisation? */
export function istInterneAdresse(
    address: string | null | undefined,
    interneDomaenen: ReadonlySet<string> | undefined,
): boolean {
    const dom = domaeneVon(address);
    return !!dom && !!interneDomaenen?.has(dom);
}

/**
 * Payload zum Anhängen einer E-Mail an einen bestehenden Kontakt.
 * `isDefault` nur, wenn noch KEINE Default-E-Mail existiert (sonst würde die
 * bestehende verdrängt); `label` optional ('Privat'|'Geschäftlich' bei Person)
 * und wird zusätzlich strukturiert als `kind` geschrieben (SP1/SP5).
 * Gibt `null`, wenn die Adresse leer ist, bereits (case-insensitiv) am Kontakt
 * hängt — ODER wenn sie zur EIGENEN Domäne gehört (B6): dann wird sie niemals
 * übernommen, unabhängig von Schreibrechten.
 */
export function buildAttachEmailPayload(args: {
    value: string;
    label?: string | null;
    existingComms: ContactCommRow[] | undefined;
    /** Eigene Domänen (B6) — fehlen sie, greift die Sperre nicht. */
    interneDomaenen?: ReadonlySet<string>;
    /**
     * C8 — „geschäftlich bei Firma X". Nur wirksam an einer PERSON mit
     * `kind='geschaeftlich'`; sonst lehnt der Server (assertCommunicationKindCompany)
     * mit 400 ab. Der Aufrufer gibt die Firma deshalb nur dann mit, wenn beides
     * zutrifft.
     */
    companyId?: number | null;
}): {
    type: "email";
    value: string;
    label?: string;
    kind?: string;
    companyId?: number;
    isDefault: boolean;
} | null {
    const value = (args.value ?? "").trim();
    if (!value) return null;
    if (istInterneAdresse(value, args.interneDomaenen)) return null;
    if (hasSameEmail(args.existingComms, value)) return null;
    const kind = kindFromLabel(args.label);
    const companyId =
        kind === "geschaeftlich" && args.companyId && args.companyId > 0 ? args.companyId : null;
    return {
        type: "email",
        value,
        ...(args.label ? { label: args.label } : {}),
        ...(kind ? { kind } : {}),
        ...(companyId ? { companyId } : {}),
        isDefault: !hasDefaultEmail(args.existingComms),
    };
}

// ── Weg 2: neuer Kontakt (B1, C1–C8) ─────────────────────────────────────────

/** Frage 1 der Neuanlage (C2). */
export type NeuTyp = "firma" | "privat";
/**
 * Frage 2 der Neuanlage (C1) — der Primärstatus des neuen Kontakts.
 *
 * PO 28.09.2026: „Interessent/Kunde bei Erstellung Zuweisung aus Mail, da muss
 * auch indirekt und Sonstige mit rein." „Sonstige" ist ein Katalogwert und geht
 * unverändert als `statusName` an die API — wie in der Vollanlage ohne Rolle.
 * „Indirekt" gibt es als Status NICHT: die Leiste „Indirekter Kontakt" (#1728)
 * wurde am 25.09.2026 zurückgenommen (#1774), ihre Rollen stehen wieder unter
 * „Sonstige". Ein Name ohne Katalogzeile würde serverseitig ersatzlos
 * verworfen — der Kontakt entstünde still ohne Status.
 */
export const NEU_STATUS_OPTIONEN = ["Interessent", "Kunde", "Sonstige"] as const;
export type NeuStatus = (typeof NEU_STATUS_OPTIONEN)[number];
/**
 * T-27 (PO 01.10.2026): Die Neuanlage fragt den Status jetzt wie die
 * Vollanlage über `KontaktStatusSchritt` — mit Bereich/Rolle unter
 * „Sonstige". Daraus entsteht über `statusFuerApi` EIN Primärstatus plus
 * Zusatzstatus. Ein bloßer Statusname (`NeuStatus`) bleibt als Kurzform erlaubt.
 */
export type StatusWahl = { statusName: string; weitereStatus?: string[] };

/** `statusName` + (nur wenn nicht leer) `weitereStatus` für den POST /contact-Body. */
function statusBody(status: StatusWahl | string): Record<string, unknown> {
    if (typeof status === "string") return { statusName: status };
    const weitere = (status.weitereStatus ?? []).filter(Boolean);
    return { statusName: status.statusName, ...(weitere.length ? { weitereStatus: weitere } : {}) };
}

/**
 * T-27 (PO 01.10.2026): Vorbelegung „privat | geschäftlich", wenn eine Person
 * aus der Suche übernommen wird — geschäftlich bei Status „Sonstige", privat bei
 * Interessent oder Kunde. Ohne Statusgruppe entscheidet der Statusname. An
 * Nicht-Personen gibt es keine Art (A15) — dort bleibt es beim neutralen „privat".
 */
export function adresseArtVorbelegung(t: {
    art?: string | null;
    statusGruppe?: string | null;
    statusName?: string | null;
}): KommArt {
    if (t.art !== "ap" && t.art !== "privat") return "privat";
    if (t.statusGruppe) return t.statusGruppe === "sonstige" ? "geschaeftlich" : "privat";
    const name = (t.statusName ?? "").trim();
    return name === "Kunde" || name === "Interessent" ? "privat" : "geschaeftlich";
}

/** Ein Knopf der Fußzeile des Zuweisen-Fensters. */
export type FussKnopf = {
    /** `nur` = ohne Vorgang abschließen, `weiter` = zum Vorgang, `einzig` = einziger Ausgang. */
    id: "nur" | "weiter" | "einzig";
    label: string;
    enabled: boolean;
    primaer: boolean;
};

/**
 * T-27 (PO 01.10.2026): Die Fußzeile hat genau die Ausgänge, die gerade passen:
 *   Treffer aus der Suche → „Nur Zuweisen | Weiter"
 *   Neuanlage offen       → „Nur Anlegen | Weiter"
 *   weder noch            → „Weiter" (fragt „Ohne Kontakt fortfahren?")
 * „Abbrechen" und „Ohne Kontakt fortfahren" gibt es nicht mehr — geschlossen
 * wird über das X der Titelleiste. Die Modi ohne Mail-Ausgang (Visitenkarte,
 * weiterer Kontakt) behalten ihren einen Knopf.
 */
export function fusszeileFuer(a: {
    treffer: boolean;
    neuOffen: boolean;
    neuOk: boolean;
    busy: boolean;
    modus: "zuweisen" | "nur-anlegen" | "zusaetzlich";
}): FussKnopf[] {
    const bereit = a.treffer || (a.neuOffen && a.neuOk);
    if (a.modus === "nur-anlegen") {
        return [
            {
                id: "einzig",
                label: a.busy ? "Wird angelegt …" : a.treffer ? "Kontakt öffnen" : "Kontakt anlegen",
                enabled: !a.busy && bereit,
                primaer: true,
            },
        ];
    }
    if (a.modus === "zusaetzlich") {
        return [
            {
                id: "einzig",
                label: a.busy
                    ? a.treffer
                        ? "Wird zugewiesen …"
                        : "Wird angelegt …"
                    : a.treffer
                      ? "Zusätzlich zuweisen"
                      : "Anlegen & zusätzlich zuweisen",
                enabled: !a.busy && bereit,
                primaer: true,
            },
        ];
    }
    const weiter: FussKnopf = { id: "weiter", label: "Weiter", enabled: !a.busy, primaer: true };
    if (a.treffer) {
        return [{ id: "nur", label: "Nur Zuweisen", enabled: !a.busy, primaer: false }, weiter];
    }
    if (a.neuOffen) {
        return [{ id: "nur", label: "Nur Anlegen", enabled: !a.busy && a.neuOk, primaer: false }, weiter];
    }
    return [weiter];
}
/** Strukturierte Art eines Kommunikationswegs (ASCII wie in der Spalte `kind`). */
export type KommArt = "privat" | "geschaeftlich";

/** Eingaben des Firmen-Zweigs (ohne Ansprechpartner-Block). */
export type FirmaWerte = {
    name: string;
    gesellschaftsform: string;
    legalFormId?: string;
    email?: string;
    telefon?: string;
    mobil?: string;
    /** T-36: Unternehmenssitz, z. B. aus der Signatur. Alles optional. */
    strasse?: string;
    plz?: string;
    ort?: string;
};

/** Eingaben des Privat-Zweigs. */
export type PrivatWerte = {
    lastName: string;
    firstName?: string;
    email?: string;
    emailArt: KommArt;
    telefon?: string;
    telefonArt: KommArt;
    mobil?: string;
    mobilArt: KommArt;
};

/** Eingaben des Ansprechpartner-Blocks (immer geschäftlich — er hängt an der Firma). */
export type ApWerte = {
    lastName: string;
    firstName?: string;
    email?: string;
    telefon?: string;
    mobil?: string;
    /**
     * Verantwortlichkeit an der Firma — dieselbe Eingabe wie die Vollanlage bei
     * „Verbindung zu Unternehmen" (bis 01.10.2026 der Freitext „Funktion", T-36).
     */
    verant?: VerantwortlichkeitValue | null;
};

/**
 * Das Ergebnis EINER Zuweisung — der Vertrag zwischen Popup und Aufrufer (B5).
 *
 * `kontakt` ist der ADRESSTRÄGER: bei einem neu angelegten Ansprechpartner also
 * der AP, nicht die Firma. Auf wen ein Vorgang läuft, entscheidet
 * `vorgangKontaktAus` — im Popup ist das der gewählte Kontakt, die Firma nur bei
 * sichtbar gewähltem „geschäftlich" (`vorgangAufFirma`). Die Chips der Mail-Leiste folgen
 * ihrer eigenen Regel (`resolveCandidateAssignment`).
 */
export type MailZuweisungErgebnis = {
    /** Kontakt, dem die MAIL bzw. die Adresse zugewiesen wird. null = ohne Kontakt. */
    kontakt: KontaktRef | null;
    /**
     * Die bei „geschäftlich" gewählte Firma (bei mehreren Firmen die GEWÄHLTE),
     * sonst null. Allein trägt sie den Vorgang nicht — siehe `vorgangAufFirma`.
     */
    firma: KontaktRef | null;
    /**
     * T-8: Im Popup ist an einer Person mit Firmenbezug „geschäftlich" bei
     * `firma` gewählt. Nur dann trägt eine BESTEHENDE Firma den Vorgang statt
     * der gewählten Person. `false` = ausdrücklich „privat"; fehlt das Feld,
     * stand die Frage nicht und es gilt der gewählte Kontakt.
     */
    vorgangAufFirma?: boolean;
    weiter: "zuweisen" | "vorgang";
    adresseUebernommen: boolean;
    angelegt: Array<"firma" | "ap" | "privat">;
};

/**
 * Vorgangs-Kunde aus dem Zuweisungs-Ergebnis: der Kontakt, den der Nutzer
 * GEWÄHLT hat.
 *
 * PO 28.09.2026: „in Mails, wenn ich einen Vorgang erstellen möchte für eine
 * Person, die zu einer Firma gehört, dann steht bei Kontakt 4sight IT Security,
 * obwohl ich ja die Person ausgewählt habe." Bis dahin wurde ein
 * Ansprechpartner hier still auf seine Firma umgelegt (Annahme A6). Das gilt
 * nicht mehr — die Firma bleibt über die Beziehung des Ansprechpartners
 * erreichbar und lässt sich im Vorgang als weiterer Kontakt dazunehmen.
 *
 * Ausnahmen, in denen die Firma den Vorgang trägt:
 *  - Die Firma wurde in DIESEM Schritt neu angelegt (Firmen-Anlage mit
 *    Ansprechpartner-Block) — sie ist das, was der Nutzer angelegt hat, der
 *    Ansprechpartner ist dort nur ihr Zusatz.
 *  - Der Nutzer hat im Popup „geschäftlich" bei einer (sichtbar genannten bzw.
 *    gewählten) Firma stehen (T-8, `vorgangAufFirma`). Still umgelegt wird nie.
 */
export function vorgangKontaktAus(e: MailZuweisungErgebnis): KontaktRef | null {
    if (!e.kontakt) return null;
    if (e.firma && (e.angelegt.includes("firma") || e.vorgangAufFirma)) return e.firma;
    return e.kontakt;
}

/**
 * Womit der Erstell-Flow nach „Kontakt zuweisen → Weiter" startet.
 *
 * PO 01.10.2026: Der Flow hat jetzt selbst einen Umschalter „Vorgang für:
 * <Person> | <Firma>". Damit die Person dort erhalten bleibt (als beteiligter
 * Kontakt, wenn der Vorgang auf die Firma läuft), bekommt er bei
 * „geschäftlich" die PERSON plus die Vorgabe „Firma" — nicht nur die Firma wie
 * `vorgangKontaktAus`. Die im Popup getroffene Wahl gilt im Flow wie von Hand
 * gewählt; stand die Frage nicht, entscheidet der Flow nach seiner Regel.
 * Eine in diesem Schritt NEU angelegte Firma trägt den Vorgang wie bisher.
 */
export function vorgangStartAus(e: MailZuweisungErgebnis): {
    kontakt: KontaktRef | null;
    vorgangFuer?: VorgangFuerVorgabe;
} {
    if (!e.kontakt) return { kontakt: null };
    if (e.firma && e.angelegt.includes("firma")) return { kontakt: e.firma };
    if (e.firma && e.vorgangAufFirma) return { kontakt: e.kontakt, vorgangFuer: { auf: "firma", firma: e.firma } };
    if (e.vorgangAufFirma === false) return { kontakt: e.kontakt, vorgangFuer: { auf: "person" } };
    return { kontakt: e.kontakt };
}

// ── C6: Pflichtfelder — nie strenger als die Vollanlage ──────────────────────
// Abgeglichen mit `newCompanyRequired.companyRequiredChecks` (Name + Rechtsform)
// bzw. `newPrivateRequired.privateRequiredChecks` (Nachname + Vorname) und den
// FO/BO-Toren der beiden Vollanlage-Masken:
//   Firma  = Name + Rechtsform + (FO ODER BO)   ← NewCompanyContactForm.foOrBoOk
//   Privat = Nachname                            ← kein FO/BO-Gate, Vorname s. A5
//   AP     = Nachname                            ← FO/BO erbt er von der Firma
// E-Mail ist in KEINEM Zweig mehr Pflicht. Der Vorname bleibt im Personen-Zweig
// optional (Annahme A5): die Minimal-Masken „Mensch fürs Unternehmen" verlangen
// ihn ebenfalls nicht, und aus einer Mail kennt man oft nur den Nachnamen.

const gefuellt = (v?: string): boolean => (v ?? "").trim() !== "";

export function firmaPflichtOk(w: FirmaWerte, own: AssignOwnership): boolean {
    return gefuellt(w.name) && gefuellt(w.gesellschaftsform) && ownershipSet(own);
}

export function privatPflichtOk(w: PrivatWerte): boolean {
    return gefuellt(w.lastName);
}

export function apPflichtOk(w: ApWerte): boolean {
    return gefuellt(w.lastName);
}

// ── Kommunikationszeilen ─────────────────────────────────────────────────────

/** Eine Kommunikationszeile im POST /contact-Body. */
export type KommZeile = {
    type: string;
    label?: string;
    kind?: KommArt;
    companyId?: number;
    value: string;
    isDefault: boolean;
};

/** Anzeige-Label zur strukturierten Art (der Freitext bleibt der Fallback-Reader). */
const ART_LABEL: Record<KommArt, string> = {
    privat: "Privat",
    geschaeftlich: "Geschäftlich",
};

/**
 * C4/C8 — Kommunikationszeilen einer PERSON: jede Zeile trägt ihre Art, und
 * jede GESCHÄFTLICHE Zeile zusätzlich die `companyId` der Firma, zu der sie
 * gehört. Genau das ist C8: „die geschäftliche Adresse der konkreten Firma
 * zuordnen" — bisher schrieb das nur der Detail-Dialog.
 *
 * `isDefault` genau einmal je Typ (UQ_contactComm_default_per_type).
 */
export function buildPersonKommunikation(
    w: {
        email?: string;
        emailArt: KommArt;
        telefon?: string;
        telefonArt: KommArt;
        mobil?: string;
        mobilArt: KommArt;
    },
    companyId: number | null,
    /** T-43/B6: eigene Domänen — eine interne E-Mail-Adresse wird nie gespeichert. */
    interneDomaenen?: ReadonlySet<string>,
): KommZeile[] {
    const out: KommZeile[] = [];
    const add = (type: string, value: string | undefined, art: KommArt) => {
        const v = (value ?? "").trim();
        if (!v) return;
        if (type === "email" && istInterneAdresse(v, interneDomaenen)) return;
        const firma = art === "geschaeftlich" && companyId && companyId > 0 ? companyId : null;
        out.push({
            type,
            label: ART_LABEL[art],
            kind: art,
            ...(firma ? { companyId: firma } : {}),
            value: v,
            isDefault: !out.some((z) => z.type === type),
        });
    };
    add("phone", w.telefon, w.telefonArt);
    add("mobile", w.mobil, w.mobilArt);
    add("email", w.email, w.emailArt);
    return out;
}

/**
 * C4 — Kommunikationszeilen einer FIRMA: KEIN `kind`, KEINE `companyId`.
 * Der Server nullt beides an Nicht-Personen ohnehin
 * (`sanitizeCommunicationKindCompany`); mitgeschickt wäre es nur Rauschen.
 */
export function buildFirmaKommunikation(
    w: {
        email?: string;
        telefon?: string;
        mobil?: string;
    },
    /** T-43/B6: eigene Domänen — eine interne E-Mail-Adresse wird nie gespeichert. */
    interneDomaenen?: ReadonlySet<string>,
): KommZeile[] {
    const out: KommZeile[] = [];
    const add = (type: string, value?: string) => {
        const v = (value ?? "").trim();
        if (!v) return;
        if (type === "email" && istInterneAdresse(v, interneDomaenen)) return;
        out.push({ type, value: v, isDefault: !out.some((z) => z.type === type) });
    };
    add("phone", w.telefon);
    add("mobile", w.mobil);
    add("email", w.email);
    return out;
}

// ── Payloads ─────────────────────────────────────────────────────────────────

/**
 * POST /contact-Body für eine neue FIRMA. `type`/`legalForm` folgen der
 * bewährten Vollanlage (buildCompanyContactCreatePayload): `type: {id, "Firma"}`
 * und `legalForm: {id, legalForm}` bzw. der Rechtsform-Text als Fallback.
 *
 * C1: `statusName` reist mit — ohne das Feld setzt der Controller still
 * „Interessent", und ein aus der Mail angelegter KUNDE wäre nicht anlegbar.
 */
export function buildFirmaPayload(
    w: FirmaWerte,
    status: StatusWahl | string,
    own: AssignOwnership,
    firmaTypeId: number | null,
    interneDomaenen?: ReadonlySet<string>,
): Record<string, unknown> {
    const name = w.name.trim();
    const gesellschaftsform = w.gesellschaftsform.trim();
    const legalFormId = w.legalFormId ? Number(w.legalFormId) : NaN;
    const legalForm =
        Number.isFinite(legalFormId) && legalFormId > 0
            ? { id: legalFormId, legalForm: gesellschaftsform }
            : gesellschaftsform;
    const comms = buildFirmaKommunikation(w, interneDomaenen);
    // T-36: Unternehmenssitz — derselbe Aufbau wie in der Vollanlage
    // (buildCompanyContactCreatePayload), nur wenn etwas eingetragen ist.
    const strasse = (w.strasse ?? "").trim();
    const plz = (w.plz ?? "").trim();
    const ort = (w.ort ?? "").trim();
    const addresses =
        strasse || plz || ort
            ? [
                  {
                      addressDescription: "Unternehmenssitz",
                      street: strasse,
                      postalCode: plz,
                      city: ort,
                      ortsteil: "",
                      country: "",
                      type: "private",
                      isDefault: true,
                  },
              ]
            : [];
    return {
        lastname: name,
        displayName: name,
        type: firmaTypeId != null ? { id: firmaTypeId, type: "Firma" } : { id: 1, type: "Firma" },
        ...statusBody(status),
        ...(gesellschaftsform ? { legalForm } : {}),
        ...(comms.length ? { communications: comms } : {}),
        ...(addresses.length ? { addresses } : {}),
        ...ownershipBody(own),
    };
}

/**
 * POST /contact-Body für einen neuen PRIVATKONTAKT (ContactType „Person").
 * `companyId` ist die im Privat-Zweig gewählte Firma (C3): geschäftliche Zeilen
 * werden ihr zugeordnet (C8). Ohne gewählte Firma bleibt sie null.
 */
export function buildPrivatPayload(
    w: PrivatWerte,
    status: StatusWahl | string,
    own: AssignOwnership,
    personTypeId: number | null,
    companyId: number | null,
    interneDomaenen?: ReadonlySet<string>,
): Record<string, unknown> {
    const lastname = w.lastName.trim();
    const firstName = (w.firstName ?? "").trim();
    const comms = buildPersonKommunikation(w, companyId, interneDomaenen);
    return {
        lastname,
        displayName: firstName ? `${lastname}, ${firstName}` : lastname,
        ...(firstName ? { firstName } : {}),
        ...(personTypeId != null ? { type: { id: personTypeId } } : {}),
        ...statusBody(status),
        ...(comms.length ? { communications: comms } : {}),
        ...ownershipBody(own),
    };
}

/**
 * EIN MenschItem für attach-menschen (C5): legt den Ansprechpartner als
 * „Mensch fürs Unternehmen" an, verknüpft ihn mit der Firma und erbt FO/BO
 * serverseitig. Telefon/Mobil/E-Mail gehen als `…Work`-Felder mit — die Zeilen
 * entstehen dadurch als geschäftlich und tragen (seit der Backend-Ergänzung zu
 * C8) die `companyId` der Firma. `isNew: true`, damit keine nicht existente
 * `relationId` validiert wird.
 */
export function buildApMenschItem(i: {
    firmaId: number;
    lastName: string;
    firstName?: string;
    email?: string;
    telefon?: string;
    mobil?: string;
    /** T-36: Funktion im Unternehmen — landet als Freitext an der Verbindung. */
    funktion?: string;
    /** T-43/B6: eigene Domänen — eine interne E-Mail-Adresse wird nie gespeichert. */
    interneDomaenen?: ReadonlySet<string>;
}): MenschItem {
    const trim = (v?: string) => (v ?? "").trim() || undefined;
    const email = trim(i.email);
    return {
        relationId: 0,
        personId: 0,
        companyId: i.firmaId,
        isMitarbeiterBav: false,
        isNew: true,
        role: "mensch",
        verantwortlichkeit: (i.funktion ?? "").trim(),
        newPersonKey: AP_NEW_PERSON_KEY,
        newPerson: {
            lastName: i.lastName.trim(),
            firstName: trim(i.firstName),
            emailWork: email && !istInterneAdresse(email, i.interneDomaenen) ? email : undefined,
            phoneWork: trim(i.telefon),
            mobileWork: trim(i.mobil),
        },
    };
}

/**
 * Die attach-menschen-Items eines neuen Ansprechpartners MIT Verantwortlichkeit
 * (01.10.2026) — dieselbe Übersetzung wie in der Vollanlage
 * (`buildMenschenItems` → `verantToItems`): je Rolle ein Item, der Haken
 * „Mensch im Unternehmen" an jedem, ein Haken ohne Rolle als eigenes Item
 * (Trägerbeziehung). Alle Items teilen EINEN `newPersonKey` — sonst entstünde
 * je Rolle eine eigene Person.
 *
 * Ohne Rolle und ohne Haken bleibt es beim bisherigen Ansprechpartner: eine
 * Verbindung „Mensch fürs Unternehmen" ohne Verantwortlichkeit. Die Person
 * soll auch dann an der Firma hängen, wenn ihre Rolle (noch) niemand kennt.
 */
export function buildApMenschItems(i: {
    firmaId: number;
    lastName: string;
    firstName?: string;
    email?: string;
    telefon?: string;
    mobil?: string;
    verant?: VerantwortlichkeitValue | null;
    interneDomaenen?: ReadonlySet<string>;
}): MenschItem[] {
    const { verant, ...rest } = i;
    const basis = buildApMenschItem(rest);
    const items = verantToItems(0, 0, i.firmaId, verant ?? null);
    if (!items.length) return [basis];
    return items.map((it) => ({
        ...it,
        isNew: true,
        newPerson: basis.newPerson,
        newPersonKey: AP_NEW_PERSON_KEY,
    }));
}

// ── C4/A5: Vorbelegung aus Signatur und Absender ─────────────────────────────

/** Die Signaturfelder, soweit die Vorbelegung sie braucht (alles optional). */
export type SignaturVorbelegung = {
    name?: { firstName: string; lastName: string } | null;
    company?: { value: string } | null;
    phone?: { value: string } | null;
    mobile?: { value: string } | null;
    /** T-36: Anschrift aus der Signatur (Firmenzeile oder eigene Zeilen). */
    address?: { street: string; zip: string; city: string; confident?: boolean } | null;
    /** T-36: Funktion/Position („Sachbearbeiterin") — immer nur ein Vorschlag. */
    position?: { value: string; confident?: boolean } | null;
} | null;

/**
 * Vorbelegung der Neuanlage: die SIGNATUR schlägt den Absendernamen, der
 * Absender ist der Fallback. Fehlt die Erkennung ganz (Cluster ERKENNUNG nicht
 * verfügbar, Antwort gescheitert), bleibt genau das übrig, was das Popup schon
 * immer hatte — der gesplittete Anzeigename.
 */
export function vorbelegungAusMail(input: {
    senderEmail: string | null;
    senderName: string | null;
    signature?: SignaturVorbelegung;
    /** Eigene Domänen — aus ihnen wird kein Firmenname geraten. */
    interneDomaenen?: Iterable<string>;
    /**
     * T-43: Ist der Absender ein Kollege? `true` (Erkennung meldet „intern") gilt
     * auch, solange die Postfächer — und damit die eigenen Domänen — noch nicht
     * geladen sind. `false` heißt: der Name gehört NICHT dem Absender, sondern
     * z. B. einer Visitenkarte, und bleibt; eine interne ADRESSE wird trotzdem
     * nicht vorbelegt (B6). Ohne Angabe entscheidet die Domäne.
     */
    intern?: boolean;
}): {
    firstName: string;
    lastName: string;
    firmenname: string;
    email: string;
    telefon: string;
    mobil: string;
    /** T-36: nur vorhanden, wenn die Signatur eine Anschrift hergibt. */
    strasse?: string;
    plz?: string;
    ort?: string;
    /** T-36: nur vorhanden, wenn die Signatur eine Funktion hergibt. */
    funktion?: string;
} {
    // T-43/B6: Schreibt ein Kollege (eigene Domäne, oft eine Weiterleitung),
    // gehören Name und Adresse NICHT dem anzulegenden Kunden — sie werden
    // nicht vorbelegt. Die Signatur bleibt erlaubt: bei internem Absender
    // liefert das Backend keine, und eine Visitenkarte ist eine eigene Quelle.
    const interneMenge = new Set(
        Array.from(input.interneDomaenen ?? [], (d) => String(d).trim().toLowerCase()),
    );
    const adresseIntern = input.intern === true || istInterneAdresse(input.senderEmail, interneMenge);
    const absenderIntern = input.intern ?? adresseIntern;
    const ausName = absenderIntern
        ? { firstName: "", lastName: "" }
        : splitSenderName(input.senderName);
    const sig = input.signature ?? null;
    const sigLast = (sig?.name?.lastName ?? "").trim();
    const sigFirst = (sig?.name?.firstName ?? "").trim();
    return {
        // Nur wenn die Signatur einen NACHNAMEN hergibt, gilt sie als Namensquelle
        // — ein Vorname allein würde den Absendernamen halb überschreiben.
        firstName: sigLast ? sigFirst : ausName.firstName,
        lastName: sigLast || ausName.lastName,
        // Firmenname: Signatur → ein Absendername, der selbst eine Firma ist →
        // Domäne der Absenderadresse (PO 28.09.2026: weniger Tipparbeit).
        firmenname:
            (sig?.company?.value ?? "").trim() ||
            (!absenderIntern && istFirmenName(input.senderName)
                ? bereinigeAbsenderName(input.senderName ?? "")
                : "") ||
            (absenderIntern ? "" : firmennameAusDomain(input.senderEmail, input.interneDomaenen ?? [])),
        email: adresseIntern ? "" : prefillEmail(input.senderEmail),
        telefon: (sig?.phone?.value ?? "").trim(),
        mobil: (sig?.mobile?.value ?? "").trim(),
        ...(sig?.address && (sig.address.zip || sig.address.street)
            ? {
                  strasse: (sig.address.street ?? "").trim(),
                  plz: (sig.address.zip ?? "").trim(),
                  ort: (sig.address.city ?? "").trim(),
              }
            : {}),
        ...((sig?.position?.value ?? "").trim() ? { funktion: (sig?.position?.value ?? "").trim() } : {}),
    };
}

/**
 * Startwert von „Firma | Privat" in der Neuanlage. Eine Nicht-Freemail-Adresse
 * spricht für „Firma". Kennt KMVS die Firma der Absenderdomäne schon (F.0),
 * ist der Absender vermutlich ein neuer Ansprechpartner dieser Firma — dann
 * startet die Anlage bei der PERSON, mit der Firma als „Ansprechpartner bei
 * Firma" vorbelegt (der Firma-Zweig legt immer eine NEUE Firma an).
 * T-43: Bei einem internen Absender (Kollege) sagt die Domäne nichts über den
 * Kunden — dann startet die Anlage auf „Privat".
 */
export function startTypNeu(i: {
    domainFirmen: number;
    senderEmail: string | null | undefined;
    intern: boolean;
}): NeuTyp {
    if (i.intern) return "privat";
    const a = (i.senderEmail ?? "").trim().toLowerCase();
    const at = a.lastIndexOf("@");
    const freemail = at < 0 || FREEMAIL_DOMAINS.has(a.slice(at + 1));
    if (i.domainFirmen > 0) return "privat";
    return !freemail ? "firma" : "privat";
}

/**
 * T-36: Der sichtbare Hinweis „Signatur erkannt: …" über einer Mail, deren
 * Absender noch keinem Kontakt zugeordnet ist. Nur, wenn die Erkennung eine
 * Signatur MIT Name oder Firma gefunden hat — bei internem Absender liefert das
 * Backend keine Signatur (`null`), dann gibt es auch keinen Hinweis.
 */
export function signaturHinweis(
    signature:
        | {
              found: boolean;
              name?: { firstName: string; lastName: string } | null;
              company?: { value: string } | null;
              phone?: { value: string } | null;
              mobile?: { value: string } | null;
          }
        | null
        | undefined,
    absenderZugeordnet: boolean,
): string | null {
    if (!signature || !signature.found || absenderZugeordnet) return null;
    const name = [signature.name?.firstName, signature.name?.lastName]
        .map((x) => (x ?? "").trim())
        .filter(Boolean)
        .join(" ");
    const firma = (signature.company?.value ?? "").trim();
    if (!name && !firma) return null;
    const nummer = (signature.phone?.value ?? signature.mobile?.value ?? "").trim();
    return `Signatur erkannt: ${[name, firma, nummer].filter(Boolean).join(" · ")}`;
}
